import openSocket from "socket.io-client";
import api from "./apiConfig";
import jwtDecode from 'vue-jwt-decode';

/* eslint-disable no-unused-vars */
class ManagedSocket {
  constructor(socketManager) {
    this.socketManager = socketManager;
    this.rawSocket = socketManager.currentSocket;
    this.callbacks = [];
    this.joins = [];

    this.rawSocket.on("connect", () => {
      if (this.rawSocket.io.opts.query?.r && !this.rawSocket.recovered) {
        const refreshJoinsOnReady = () => {
          for (const j of this.joins) {
            console.debug("refreshing join", j);
            this.rawSocket.emit(`join${j.event}`, ...j.params);
          }
          this.rawSocket.off("ready", refreshJoinsOnReady);
        };
        for (const j of this.callbacks) {
          this.rawSocket.off(j.event, j.callback);
          this.rawSocket.on(j.event, j.callback);
        }

        this.rawSocket.on("ready", refreshJoinsOnReady);
      }
    });
  }

  on(event, callback) {
    if (event === "ready" || event === "connect") {
      return this.socketManager.onReady(callback);
    }
    this.callbacks.push({ event, callback });
    return this.rawSocket.on(event, callback);
  }

  off(event, callback) {
    const i = this.callbacks.findIndex((c) => c.event === event && c.callback === callback);
    this.callbacks.splice(i, 1);
    return this.rawSocket.off(event, callback);
  }

  emit(event, ...params) {
    if (event.startsWith("join")) {
      this.joins.push({ event: event.substring(4), params });
      console.debug("Joining", { event: event.substring(4), params });
    }
    return this.rawSocket.emit(event, ...params);
  }

  disconnect() {
    for (const j of this.joins) {
      this.rawSocket.emit(`leave${j.event}`, ...j.params);
    }
    this.joins = [];
    for (const c of this.callbacks) {
      this.rawSocket.off(c.event, c.callback);
    }
    this.callbacks = [];
  }
}

class DummySocket {
  on(..._) {}
  off(..._) {}
  emit(..._) {}
  disconnect() {}
}

const socketManager = {
  currentCompanyId: -1,
  currentUserId: -1,
  currentSocket: null,
  socketReady: false,

  GetSocket(_discardCompanyId = null) {
    const tokenString = localStorage.getItem("token");
    let token;
    try {
      token = JSON.parse(tokenString);
    } catch (error) {
      token = tokenString;
    }
    if (!token) {
      return new DummySocket();
    }

    const { userId, companyId } = jwtDecode.decode(token)
    const isExpired = (token) => {
      const decodedToken = jwtDecode.decode(token);
      const currentTime = Date.now() / 1000; // tempo em segundos
      return decodedToken.exp < currentTime;
    };

    if (companyId !== this.currentCompanyId || userId !== this.currentUserId) {
      if (this.currentSocket) {
        console.debug("closing old socket - company or user changed");
        this.currentSocket.removeAllListeners();
        this.currentSocket.disconnect();
        this.currentSocket = null;
        this.currentCompanyId = null;
        this.currentUserId = null;
      }

      if (isExpired(token)) {
        console.debug("Expired token, refreshing token");

        api.get("/auth/me").then((response) => {
          console.debug("Token refreshed", response);
          window.location.reload();
        });

        return new DummySocket();
      }

      this.currentCompanyId = companyId;
      this.currentUserId = userId;

      this.currentSocket = openSocket(process.env.VUE_APP_BACKEND, {
        transports: ["websocket"],
        pingTimeout: 18000,
        pingInterval: 18000,
        query: { token },
      });

      this.currentSocket.io.on("reconnect_attempt", () => {
        this.currentSocket.io.opts.query.r = 1;
        const newToken = localStorage.getItem("token");
        if (isExpired(newToken)) {
          console.debug("Refreshing");
          window.location.reload();
        } else {
          console.debug("Using new token");
          this.currentSocket.io.opts.query.token = newToken;
        }
      });

      this.currentSocket.on("disconnect", (reason) => {
        console.debug(`socket disconnected because: ${reason}`);
        if (reason.startsWith("io server disconnect")) {
          console.debug("trying to reconnect", this.currentSocket);
          const tokenString = localStorage.getItem("token");
          let newToken;
          try {
            newToken = JSON.parse(tokenString);
          } catch (error) {
            newToken = tokenString; // Se não for um JSON válido, mantém como string
          }

          if (isExpired(newToken)) {
            console.debug("Expired token - refreshing");
            window.location.reload();
            return;
          }
          console.debug("Reconnecting using refreshed token");
          this.currentSocket.io.opts.query.token = newToken;
          this.currentSocket.io.opts.query.r = 1;
          this.currentSocket.connect();
        }
      });

      this.currentSocket.on("connect", (...params) => {
        console.debug("socket connected", params);
      });

      this.currentSocket.onAny((event, ...args) => {
        console.debug("Event: ", { socket: this.currentSocket, event, args });
      });

      this.onReady(() => {
        this.socketReady = true;
      });
    }

    return new ManagedSocket(this); // Retorna uma instância de ManagedSocket
  },

  onReady(callbackReady) {
    if (this.socketReady) {
      callbackReady();
      return;
    }

    if (!this.currentSocket) {
      return;
    }

    this.currentSocket.once("ready", () => {
      callbackReady();
    });
  },

  onConnect(callbackReady) {
    this.onReady(callbackReady);
  },
};

export default socketManager; // Exporte o objeto socketManager diretamente
